<template>
  <div class="app">
    <HeaderBar />
    <NavBar :show="showNavBar" />
    <router-view v-slot="{ Component }">
      <transition name="fade" mode="out-in">
        <!-- this will cause crazy bug if the component has multiple node -->
        <component :is="Component" :panel-type="currentPanel" :is-nav-bar-visible="showNavBar" />
      </transition>
    </router-view>

  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import HeaderBar from "./components/HeaderBar.vue";
import NavBar from "./components/NavBar.vue";
import SidePanelInfo from "./components/SidePanelInfo.vue";
import SidePanelEIA from "./components/SidePanelEIA.vue";

export default {
  components: {
    HeaderBar,
    NavBar,
    SidePanelInfo,
    SidePanelEIA,
  },
  setup() {
    const ProjectInfoScrollPosition = 10;
    const isScrolling = ref(false);
    const isViaScroll = ref(false);
    const isScrollLocked = ref(false);
    const route = useRoute();
    const router = useRouter(); // Use useRouter for programmatic navigation
    // const showNavBar = ref(false);
    const scrollPosition = ref(0);
    const isProgrammaticNavigation = ref(false);

    const currentPanel = computed(() => {
      if (route.path === "/project-info") {
        return "projectInfo";
        // } else if (route.path === "/" && route.query.info === "project-info") {
        //   return "projectInfo";
      } else if (route.name === "EIA") {
        return "environmentalImpacts";
      }
      return null;
    });

    const showNavBar = computed(() => route.name !== "Home");

    let scrollTimeout;

    // Function to lock user scroll for specified duration
    const lockScroll = (duration = 500) => {
      isScrollLocked.value = true;
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      
      setTimeout(() => {
        isScrollLocked.value = false;
        document.body.style.overflow = originalOverflow;
      }, duration);
    };

    const handleScroll = () => {
      scrollPosition.value = window.scrollY;
      
      // IGNORE scroll events during programmatic navigation to prevent conflicts
      if (isProgrammaticNavigation.value || isScrollLocked.value) {
        console.log('Ignoring scroll event during programmatic navigation or scroll lock');
        return;
      }

      // Navigate to "ProjectInfo" when on "Home" and scrolled downward past threshold
      if (
        route.name === "Home" &&
        scrollPosition.value >= ProjectInfoScrollPosition
      ) {
        isViaScroll.value = true;
        console.log("enter ProjectInfo" + isViaScroll.value);
        
        // Lock scroll for 500ms before navigation
        lockScroll(500);
        
        router.push({ name: "ProjectInfo" });
      }
    };

    onMounted(() => {
      window.addEventListener("scroll", handleScroll);
    });

    onUnmounted(() => {
      window.removeEventListener("scroll", handleScroll);
    });

    // Centralized scroll to position function
    const scrollToPosition = (position) => {
      console.log("scroll to position" + position);
      window.scrollTo({ top: position });
    };

    // Function to scroll to the start of the side panel content (scroll-target-1)
    const scrollToSidePanelStart = () => {
      const scrollTarget1 = document.getElementById('scroll-target-1');
      if (scrollTarget1) {
        // Get the position of scroll-target-1 and add a small offset for better positioning
        const rect = scrollTarget1.getBoundingClientRect();
        const targetPosition = window.scrollY + rect.top - 100; // 100px offset from top
        
        isProgrammaticNavigation.value = true;
        window.scrollTo({ 
          top: targetPosition, 
          behavior: 'smooth' 
        });
        
        setTimeout(() => {
          isProgrammaticNavigation.value = false;
        }, 1000); // Give time for smooth scroll to complete
        
        console.log('Scrolled to side panel start position (scroll-target-1):', targetPosition);
      } else {
        // Fallback to original scroll position if scroll-target-1 is not found
        console.log('scroll-target-1 not found, using fallback position');
        const targetPosition = ProjectInfoScrollPosition + 50;
        
        isProgrammaticNavigation.value = true;
        window.scrollTo({ 
          top: targetPosition, 
          behavior: 'smooth' 
        });
        
        setTimeout(() => {
          isProgrammaticNavigation.value = false;
        }, 1000);
      }
    };

    // Use Vue Router's beforeEach hook to detect programmatic navigation
    router.beforeEach((to, from, next) => {
      // If navigating to Home from ProjectInfo, disable scroll handler immediately
      if (to.name === "Home" && from.name === "ProjectInfo") {
        console.log('Detected navigation to Home from ProjectInfo - disabling scroll handler');
        isProgrammaticNavigation.value = true;
        
        // Re-enable scroll handler after navigation and scroll animation complete
        setTimeout(() => {
          isProgrammaticNavigation.value = false;
          console.log('Re-enabled scroll handler after navigation to Home');
        }, 1500); // Give extra time for navigation to complete and scroll to settle
      }
      next();
    });

    // Use Vue Router's afterEach hook for scroll adjustments
    router.afterEach((to, from) => {
      if (to.name === "ProjectInfo") {
        if (!isViaScroll.value) {
          // When navigating to ProjectInfo (not via scroll), position at start of side panel
          setTimeout(() => {
            scrollToSidePanelStart();
          }, 300); // Delay to ensure panel animation is complete
          console.log("Navigated to ProjectInfo - scrolling to panel start");
        } else {
          // When entering via scroll, also ensure we're positioned at the panel start
          setTimeout(() => {
            scrollToSidePanelStart();
          }, 100); // Shorter delay since we're already close to the right position
          console.log("Entered ProjectInfo via scroll - positioning at panel start");
        }
      } else if (to.name === "Home" && from.name === "ProjectInfo") {
        // When navigating back to Home from ProjectInfo, scroll to top to prevent auto-navigation loop
        isProgrammaticNavigation.value = true;
        scrollToPosition(0);
        setTimeout(() => {
          isProgrammaticNavigation.value = false;
        }, 1000); // Give extra time for navigation to settle
        console.log("Navigated to Home from ProjectInfo - scrolling to top");
      } else if (to.name === "EIA") {
        isProgrammaticNavigation.value = true;
        scrollToPosition(0);
        setTimeout(() => {
          isProgrammaticNavigation.value = false;
        }, 500);
      } else if (isViaScroll.value) {
        isViaScroll.value = false;
      }
    });

    //block console log in production
    if (process.env.NODE_ENV === 'production') {
      console.log = () => {};
      console.error = () => {};
      console.warn = () => {};
      console.info = () => {};
      console.debug = () => {};
    }

    return {
      showNavBar,
      currentPanel,
      scrollPosition,
      isScrolling,
      isViaScroll,
      isScrollLocked,
    };
  },
};
</script>

<style>
html {
  /* scrollbar-gutter: stable;  */
  /* reserves space when scrolling is locked */
  /* scrollbar-width: thin;  */
  /* makes scrollbar thinner. Wouldn't set it to `none` because of accessibility reasons */
  /* scrollbar-color: var(--primary-color) var(--bg-color); */
  /* custom colors makes scrollbar blend in  */
  scroll-behavior: smooth !important;
}



* {
  margin: 0;
  padding: 0;
  border: none;
}
.app {
  height: 100vh !important;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* ::-webkit-scrollbar {
  width: 6px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
  background: #008768;
}

::-webkit-scrollbar-thumb:hover {
  background: #008768;
} */

/* For webkit browsers (Chrome, Safari) */
html,
body {
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none; /* Firefox */
}

/* Hide scroll bar for webkit browsers */
html::-webkit-scrollbar,
body::-webkit-scrollbar {
  display: none;
}


</style>